// Conversation View Component
// Displays message thread with reply form and management controls

import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  User,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  MessageCircle,
  Calendar,
  ChevronUp,
  ChevronDown,
  ArrowDown,
  ArrowUp,
  Minus,
  Trash2,
  Pencil,
  Lock,
  ExternalLink,
  FileText,
  File as FileIcon,
  X,
  Check,
  Lightbulb,
  Undo2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
// import { ScrollArea } from '@/components/ui/scroll-area'; // Component not available, using div instead
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import type {
  ConversationViewProps,
  MessageFormData,
  FeedbackStatus,
  FeedbackPriority,
  FeedbackAttachment
} from '../types';
import { getSenderProfilePhotos } from '../services/feedbackService';
import { featureRequestBadge, isConfirmedFeatureRequest } from '../utils/feedbackBuckets';
import { NewMessagesDivider } from './UnreadIndicator';

// Helper function to safely format dates
const safeFormatDate = (
  dateString: string | null | undefined,
  formatFn: (date: Date, options?: any) => string,
  fallback: string = 'Unknown',
  options?: any
): string => {
  if (!dateString) return fallback;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return fallback;
    }
    return formatFn(date, options);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return fallback;
  }
};

const statusConfig = {
  open: {
    label: 'Open',
    color: [
      'bg-blue-100 dark:bg-blue-900',
      'text-blue-800 dark:text-blue-200',
      'border-blue-200 dark:border-blue-800'
    ].join(' '),
    icon: MessageCircle
  },
  in_progress: {
    label: 'In Progress',
    color: [
      'bg-yellow-100 dark:bg-yellow-900',
      'text-yellow-800 dark:text-yellow-200',
      'border-yellow-200 dark:border-yellow-800'
    ].join(' '),
    icon: Clock
  },
  resolved: {
    label: 'Resolved',
    color: [
      'bg-green-100 dark:bg-green-900',
      'text-green-800 dark:text-green-200',
      'border-green-200 dark:border-green-800'
    ].join(' '),
    icon: CheckCircle
  },
  archived: {
    label: 'Archived',
    color: [
      'bg-gray-100 dark:bg-gray-800',
      'text-gray-800 dark:text-gray-200',
      'border-gray-200 dark:border-gray-700'
    ].join(' '),
    icon: MessageCircle
  }
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-gray-900 dark:text-gray-100', icon: ArrowDown },
  medium: { label: 'Medium', color: 'text-blue-700 dark:text-blue-400', icon: Minus },
  high: { label: 'High', color: 'text-orange-700 dark:text-orange-400', icon: ArrowUp },
  urgent: { label: 'Urgent', color: 'text-red-700 dark:text-red-400', icon: AlertTriangle }
};

const chipCls = 'text-xs flex items-center gap-1 shrink-0';

/** First reply from someone else after `since`; the "New" divider goes above it */
const findFirstNewMessageId = (
  messages: ConversationViewProps['conversation']['messages'],
  currentUserId: string | undefined,
  since: string | undefined
): string | undefined => {
  if (!since || !messages) return undefined;
  const sinceTime = new Date(since).getTime();
  return messages.find(
    m =>
      m.sender_id !== currentUserId &&
      m.message_type !== 'admin_note' &&
      new Date(m.created_at).getTime() > sinceTime
  )?.id;
};

const DEFAULT_FEATURE_REQUEST_NOTE =
  "Thanks for the suggestion! We've logged this as a feature request for our product team. " +
  "We'll update you here when it's available.";

export const ConversationView: React.FC<ConversationViewProps> = ({
  conversation,
  currentUserId,
  onSendMessage,
  onStatusUpdate,
  onPriorityUpdate,
  canManage = false,
  loading = false,
  onCloseFeedback,
  onDeleteFeedback,
  onEditMessage,
  onDeleteMessage,
  onMoveToFeatureRequests,
  newMessagesSince,
  isOwner = false
}) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showFeatureRequestConfirm, setShowFeatureRequestConfirm] = useState(false);
  const [featureRequestNote, setFeatureRequestNote] = useState(DEFAULT_FEATURE_REQUEST_NOTE);
  const [isMovingToFeatureRequests, setIsMovingToFeatureRequests] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | undefined>();
  const [editingMessageText, setEditingMessageText] = useState('');
  const [deletingMessageId, setDeletingMessageId] = useState<string | undefined>();
  const [isEditingSubmitting, setIsEditingSubmitting] = useState(false);
  const [senderPhotos, setSenderPhotos] = useState<Record<string, string>>({});

  // Fetch profile photos for all unique senders
  useEffect(() => {
    const senderIds = [
      ...new Set(conversation.messages?.map(m => m.sender_id).filter(Boolean) || [])
    ];
    if (currentUserId && !senderIds.includes(currentUserId)) senderIds.push(currentUserId);
    if (senderIds.length > 0) {
      getSenderProfilePhotos(senderIds).then(setSenderPhotos);
    }
  }, [conversation.messages, currentUserId]);

  // Admin can edit/delete messages only when conversation is open or in_progress
  const canEditMessages =
    canManage &&
    ['open', 'in_progress'].includes(conversation.status) &&
    !!onEditMessage &&
    !!onDeleteMessage;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation.messages]);

  // Auto-focus textarea when conversation changes
  useEffect(() => {
    if (conversation && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [conversation.id]);

  const handleSendMessage = async () => {
    if (!message.trim() || isSubmitting) return;

    const messageData: MessageFormData = {
      message: message.trim(),
      attachments: [] // TODO: Implement file attachments
    };

    try {
      setIsSubmitting(true);
      await onSendMessage(messageData);
      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenFeatureRequestConfirm = () => {
    setFeatureRequestNote(DEFAULT_FEATURE_REQUEST_NOTE);
    setShowFeatureRequestConfirm(true);
  };

  // An empty note moves without posting a reply, so no email goes to the dealer
  const handleConfirmMoveToFeatureRequests = async (note: string) => {
    if (!onMoveToFeatureRequests || isMovingToFeatureRequests) return;

    try {
      setIsMovingToFeatureRequests(true);
      await onMoveToFeatureRequests(note.trim());
      setShowFeatureRequestConfirm(false);
    } catch (error) {
      console.error('Error moving to feature requests:', error);
    } finally {
      setIsMovingToFeatureRequests(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStartEdit = (msgId: string, msgText: string) => {
    setEditingMessageId(msgId);
    setEditingMessageText(msgText);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(undefined);
    setEditingMessageText('');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editingMessageText.trim() || !onEditMessage || isEditingSubmitting)
      return;

    try {
      setIsEditingSubmitting(true);
      await onEditMessage(editingMessageId, editingMessageText.trim());
      setEditingMessageId(undefined);
      setEditingMessageText('');
    } catch (error) {
      console.error('Error editing message:', error);
    } finally {
      setIsEditingSubmitting(false);
    }
  };

  const handleConfirmDeleteMessage = async () => {
    if (!deletingMessageId || !onDeleteMessage) return;

    try {
      await onDeleteMessage(deletingMessageId);
      setDeletingMessageId(undefined);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    }
    if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const isMessageEdited = (msg: { created_at: string; updated_at: string }) => {
    if (!msg.updated_at || !msg.created_at) return false;
    const created = new Date(msg.created_at).getTime();
    const updated = new Date(msg.updated_at).getTime();
    return updated - created > 1000; // More than 1 second difference
  };

  const getSenderName = (senderId: string) => {
    const message = conversation.messages?.find(m => m.sender_id === senderId);
    if (message) {
      // Prefer full_name, then first+last, then email
      if (message.sender_full_name) {
        return message.sender_full_name;
      }
      if (message.sender_first_name && message.sender_last_name) {
        return `${message.sender_first_name} ${message.sender_last_name}`;
      }
      return message.sender_email || 'Unknown User';
    }
    return 'Unknown User';
  };

  const isCurrentUserMessage = (senderId: string) => {
    return currentUserId === senderId;
  };

  if (!conversation) {
    return (
      <div className='flex flex-col h-full items-center justify-center'>
        <MessageCircle className='h-12 w-12 text-muted-foreground mb-4' />
        <p className='text-muted-foreground'>No conversation selected</p>
      </div>
    );
  }

  const displayedMessages = showAllMessages
    ? conversation.messages || []
    : (conversation.messages || []).slice(-10); // Show last 10 messages by default

  const firstNewMessageId = findFirstNewMessageId(
    displayedMessages,
    currentUserId,
    newMessagesSince
  );

  if (loading) {
    return (
      <div className='flex flex-col h-full'>
        {/* Header Skeleton */}
        <Card className='mb-4'>
          <CardHeader className='pb-4'>
            <div className='flex items-center justify-between'>
              <div className='space-y-2'>
                <Skeleton className='h-6 w-64' />
                <Skeleton className='h-4 w-32' />
              </div>
              <div className='flex gap-2'>
                <Skeleton className='h-8 w-20' />
                <Skeleton className='h-8 w-24' />
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Messages Skeleton */}
        <div className='flex-1 space-y-4'>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className='flex gap-3'>
              <Skeleton className='h-8 w-8 rounded-full' />
              <div className='flex-1 space-y-2'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-16 w-full' />
              </div>
            </div>
          ))}
        </div>

        {/* Input Skeleton */}
        <Card className='mt-4'>
          <CardContent className='p-4'>
            <Skeleton className='h-20 w-full' />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className='flex flex-col items-center justify-center h-full text-center'>
        <MessageCircle className='h-12 w-12 text-muted-foreground mb-4' />
        <h3 className='text-lg font-semibold mb-2'>No conversation selected</h3>
        <p className='text-muted-foreground'>
          Select a conversation from the list to view messages.
        </p>
      </div>
    );
  }

  const statusInfo = statusConfig[conversation.status] || statusConfig.open;
  const priorityInfo = priorityConfig[conversation.priority] || priorityConfig.medium;
  const triggerCls = 'h-7 text-xs border-primary/30 bg-background shadow-sm';
  const isFeatureRequest = isConfirmedFeatureRequest(conversation);
  // Dealers see "Feature request" instead of "In Progress" once an admin confirms it
  const badgeInfo = isFeatureRequest ? featureRequestBadge : statusInfo;
  const StatusIcon = badgeInfo?.icon || MessageCircle;
  const canMoveToFeatureRequests =
    canManage &&
    !!onMoveToFeatureRequests &&
    !isFeatureRequest &&
    conversation.status !== 'archived';
  const FeatureRequestIcon = featureRequestBadge.icon;
  // Admin replies are emailed to whoever submitted the feedback
  const reporterName =
    conversation.user_full_name || conversation.user_email || 'the person who reported it';
  const featureRequestChip = (
    <Badge variant='outline' className={cn(chipCls, featureRequestBadge.color)}>
      <FeatureRequestIcon className='h-3 w-3' />
      {featureRequestBadge.label}
    </Badge>
  );

  return (
    <div className='flex flex-col h-full'>
      {/* Conversation Header */}
      <div className='mb-3 bg-muted/30 rounded-lg border p-2 space-y-2 shrink-0'>
        {/* Row 1: Title + Priority */}
        <div className='flex items-center gap-2 min-w-0'>
          <span className='text-xs font-medium text-muted-foreground shrink-0'>Details</span>
          <span className='text-muted-foreground/40'>|</span>
          <h2 className='text-base font-semibold truncate'>{conversation.title}</h2>
          {/* Priority - top right */}
          <div className='ml-auto shrink-0'>
            {canManage ? (
              <Select
                value={conversation.priority}
                onValueChange={(value: FeedbackPriority) => {
                  onPriorityUpdate?.(value);
                }}
              >
                <SelectTrigger className={cn('w-36 h-9', triggerCls)}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(priorityConfig).map(([priority, config]) => {
                    const PriorityIcon = config.icon;
                    return (
                      <SelectItem key={priority} value={priority}>
                        <div className={cn('flex items-center gap-2', config.color)}>
                          <PriorityIcon className='h-4 w-4' />
                          <span className='font-medium'>{config.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <div className='flex items-center gap-1.5'>
                {priorityInfo.icon && (
                  <priorityInfo.icon className={cn('h-4 w-4', priorityInfo.color)} />
                )}
                <span className={cn('text-sm font-semibold', priorityInfo.color)}>
                  {priorityInfo.label}
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Row 2: Status, category, date, user, actions */}
        <div className='flex items-center gap-2 min-w-0 flex-wrap'>
          {canManage ? (
            <Select
              value={conversation.status}
              onValueChange={(value: FeedbackStatus) => {
                onStatusUpdate?.(value);
              }}
            >
              <SelectTrigger className={cn('w-28 h-7 text-xs shadow-sm', statusInfo.color)}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(statusConfig).map(([status, config]) => {
                  const IconComponent = config.icon;
                  return (
                    <SelectItem
                      key={status}
                      value={status}
                      className={cn('rounded-md', config.color)}
                    >
                      <div className='flex items-center gap-2'>
                        <IconComponent className='h-3 w-3' />
                        {config.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          ) : (
            <Badge
              variant='outline'
              className={cn('text-xs flex items-center gap-1 shrink-0', badgeInfo.color)}
            >
              <StatusIcon className='h-3 w-3' />
              {badgeInfo.label}
            </Badge>
          )}
          {/* Admins keep the status select, so the feature request chip sits beside it */}
          {canManage && isFeatureRequest && featureRequestChip}
          {!isFeatureRequest && conversation.category_display_name && (
            <Badge variant='secondary' className='text-xs shrink-0'>
              {conversation.category_display_name}
            </Badge>
          )}
          {canManage && isFeatureRequest && onStatusUpdate && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => onStatusUpdate('open')}
              className='h-7 text-xs shrink-0'
              title='Set back to Open so it shows under Active again'
            >
              <Undo2 className='h-3 w-3 mr-1' />
              Move back to Active
            </Button>
          )}
          {canMoveToFeatureRequests && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleOpenFeatureRequestConfirm}
              className='h-7 text-xs shrink-0'
            >
              <Lightbulb className='h-3 w-3 mr-1' />
              Move to Feature requests
            </Button>
          )}
          {/* Right column: Date, Name (Tenant), Actions stacked */}
          <div className='flex flex-col items-end gap-0.5 text-xs text-muted-foreground shrink-0 ml-auto'>
            {/* Date */}
            <div className='flex items-center gap-1'>
              <Calendar className='h-3 w-3' />
              <span className='whitespace-nowrap'>
                {safeFormatDate(conversation.created_at, format, 'Unknown date', 'MMM d, yyyy')}
              </span>
            </div>

            {/* User info with tenant */}
            <div className='flex items-center gap-1'>
              <User className='h-3 w-3' />
              <span className='truncate max-w-[200px]'>
                {conversation.user_full_name || conversation.user_email || 'Unknown user'}
                {conversation.tenant_name && (
                  <span className='text-muted-foreground/60'> ({conversation.tenant_name})</span>
                )}
              </span>
            </div>

            {/* User Actions - Only show for feedback owner */}
            {isOwner && conversation.status !== 'archived' && (
              <div className='flex items-center gap-1 mt-1'>
                {conversation.status !== 'resolved' && onCloseFeedback && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => setShowCloseConfirm(true)}
                    className='h-6 text-xs'
                  >
                    <Lock className='h-3 w-3 mr-1' />
                    Mark Resolved
                  </Button>
                )}
                {onDeleteFeedback && (
                  <Button
                    variant='destructive'
                    size='sm'
                    onClick={() => setShowDeleteConfirm(true)}
                    className='h-6 text-xs'
                  >
                    <Trash2 className='h-3 w-3 mr-1' />
                    Delete
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className='flex-1 flex flex-col min-h-0'>
        {/* Show More/Less Toggle */}
        {conversation.messages && conversation.messages.length > 10 && (
          <div className='flex justify-center mb-4'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => setShowAllMessages(!showAllMessages)}
              className='h-8'
            >
              {showAllMessages ? (
                <>
                  <ChevronUp className='h-4 w-4 mr-1' />
                  Show Recent Messages
                </>
              ) : (
                <>
                  <ChevronDown className='h-4 w-4 mr-1' />
                  Show All Messages ({conversation.messages?.length || 0})
                </>
              )}
            </Button>
          </div>
        )}

        <div className='flex-1 overflow-y-auto pr-4'>
          <div className='space-y-4'>
            {displayedMessages.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-8 text-center'>
                <MessageCircle className='h-8 w-8 text-muted-foreground mb-2' />
                <p className='text-sm text-muted-foreground'>No messages yet</p>
              </div>
            ) : (
              displayedMessages.map((msg, index) => {
                const isCurrentUser = isCurrentUserMessage(msg.sender_id);
                const isAdmin =
                  msg.sender_first_name?.includes('Admin') || msg.sender_email?.includes('admin');
                const senderName = getSenderName(msg.sender_id);

                const row = (
                  <div
                    key={msg.id}
                    className={cn('flex gap-3', isCurrentUser ? 'justify-end' : 'justify-start')}
                  >
                    {/* Avatar */}
                    {!isCurrentUser && (
                      <div className='flex-shrink-0'>
                        <Avatar className='h-8 w-8'>
                          <AvatarImage src={senderPhotos[msg.sender_id]} />
                          <AvatarFallback
                            className={cn(
                              'text-white text-xs font-semibold',
                              isAdmin ? 'bg-red-500' : 'bg-primary'
                            )}
                          >
                            {isAdmin ? (
                              <Shield className='h-4 w-4' />
                            ) : (
                              senderName.charAt(0).toUpperCase()
                            )}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    )}

                    {/* Message Content */}
                    <div
                      className={cn(
                        'flex flex-col',
                        editingMessageId === msg.id ? 'w-[60%]' : 'max-w-[70%]',
                        isCurrentUser ? 'items-end' : 'items-start'
                      )}
                    >
                      {/* Sender Name and Time */}
                      <div
                        className={cn(
                          'flex items-center gap-2 text-xs text-muted-foreground mb-1',
                          isCurrentUser ? 'flex-row-reverse' : 'flex-row'
                        )}
                      >
                        <span className='font-medium'>{isAdmin ? 'Admin' : senderName}</span>
                        <span>•</span>
                        <span>
                          {safeFormatDate(msg.created_at, formatDistanceToNow, 'Unknown time', {
                            addSuffix: true
                          })}
                        </span>
                      </div>

                      {/* Message Bubble */}
                      <div
                        className={cn(
                          'group/message relative',
                          editingMessageId === msg.id && 'w-full'
                        )}
                      >
                        {editingMessageId === msg.id ? (
                          /* Edit Mode */
                          <div className='space-y-2'>
                            <Textarea
                              value={editingMessageText}
                              onChange={e => setEditingMessageText(e.target.value)}
                              onKeyDown={handleEditKeyPress}
                              className='min-h-[120px] w-full resize-y text-sm'
                              disabled={isEditingSubmitting}
                              autoFocus
                            />
                            <div className='flex items-center gap-2'>
                              <Button
                                size='sm'
                                className='h-7 text-xs'
                                onClick={handleSaveEdit}
                                disabled={!editingMessageText.trim() || isEditingSubmitting}
                              >
                                <Check className='h-3 w-3 mr-1' />
                                {isEditingSubmitting ? 'Saving...' : 'Save'}
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-7 text-xs'
                                onClick={handleCancelEdit}
                                disabled={isEditingSubmitting}
                              >
                                <X className='h-3 w-3 mr-1' />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* Normal Display */
                          <div
                            className={cn(
                              'px-4 py-2 rounded-lg text-base',
                              isCurrentUser
                                ? 'bg-primary text-primary-foreground'
                                : isAdmin
                                  ? [
                                      'bg-red-50 dark:bg-red-950',
                                      'text-red-900 dark:text-red-100',
                                      'border border-red-200 dark:border-red-800'
                                    ]
                                  : [
                                      'bg-gray-100 dark:bg-gray-800 text-foreground',
                                      'border border-gray-200 dark:border-gray-700 shadow-sm'
                                    ],
                              msg.message_type === 'system' && [
                                'bg-blue-50 dark:bg-blue-950',
                                'text-blue-900 dark:text-blue-100',
                                'border border-blue-200 dark:border-blue-800'
                              ]
                            )}
                          >
                            <p className='whitespace-pre-wrap'>{msg.message}</p>

                            {/* Edited indicator */}
                            {isMessageEdited(msg) && (
                              <span className='text-xs opacity-60 mt-1 block'>(edited)</span>
                            )}

                            {/* System Message Indicator */}
                            {msg.message_type === 'system' && (
                              <div className='flex items-center gap-1 mt-2 text-xs'>
                                <Shield className='h-3 w-3' />
                                System Message
                              </div>
                            )}
                          </div>
                        )}

                        {/* Edit/Delete Actions - visible on hover, admin only, open/in_progress only */}
                        {canEditMessages &&
                          editingMessageId !== msg.id &&
                          msg.message_type !== 'system' && (
                            <div
                              className={cn(
                                'absolute -top-3 opacity-0 group-hover/message:opacity-100 transition-opacity',
                                'flex items-center gap-1 bg-background border rounded-md shadow-sm p-0.5',
                                isCurrentUser ? 'left-0' : 'right-0'
                              )}
                            >
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-6 w-6 p-0'
                                onClick={() => handleStartEdit(msg.id, msg.message)}
                                title='Edit message'
                              >
                                <Pencil className='h-3 w-3 text-muted-foreground' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                className='h-6 w-6 p-0'
                                onClick={() => setDeletingMessageId(msg.id)}
                                title='Delete message'
                              >
                                <Trash2 className='h-3 w-3 text-destructive' />
                              </Button>
                            </div>
                          )}
                      </div>

                      {/* Message Attachments */}
                      {msg.attachments &&
                        Array.isArray(msg.attachments) &&
                        msg.attachments.length > 0 && (
                          <div
                            className={cn(
                              'mt-2 grid gap-2',
                              msg.attachments.length === 1 ? 'grid-cols-1' : 'grid-cols-2'
                            )}
                          >
                            {msg.attachments.map(
                              (attachment: FeedbackAttachment, attachIdx: number) => {
                                const isImage = attachment.content_type?.startsWith('image/');
                                const isPdf = attachment.content_type === 'application/pdf';
                                const AttachmentIcon = isPdf ? FileText : FileIcon;

                                return (
                                  <div
                                    key={attachment.id || attachIdx}
                                    className='relative group rounded-lg overflow-hidden border bg-background'
                                  >
                                    <a
                                      href={attachment.url}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      className='block'
                                    >
                                      {isImage ? (
                                        // Image attachment
                                        <>
                                          <img
                                            src={attachment.url}
                                            alt={attachment.original_filename || 'Attachment'}
                                            className={[
                                              'w-full h-auto max-h-[28rem]',
                                              'object-contain hover:opacity-90',
                                              'transition-opacity'
                                            ].join(' ')}
                                            loading='lazy'
                                          />
                                          {/* Hover overlay */}
                                          <div className='absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100'>
                                            <ExternalLink className='h-6 w-6 text-white' />
                                          </div>
                                          {/* Filename tooltip */}
                                          <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity'>
                                            <p className='text-xs text-white truncate'>
                                              {attachment.original_filename}
                                            </p>
                                          </div>
                                        </>
                                      ) : (
                                        // Document attachment
                                        <div className='flex items-center gap-3 p-3 hover:bg-accent transition-colors'>
                                          <AttachmentIcon className='h-8 w-8 text-muted-foreground shrink-0' />
                                          <div className='min-w-0 flex-1'>
                                            <p className='text-sm font-medium truncate'>
                                              {attachment.original_filename}
                                            </p>
                                            <p className='text-xs text-muted-foreground'>
                                              {attachment.content_type
                                                ?.split('/')[1]
                                                ?.toUpperCase() || 'File'}
                                              {attachment.size &&
                                                ` • ${(attachment.size / 1024).toFixed(0)} KB`}
                                            </p>
                                          </div>
                                          <ExternalLink className='h-4 w-4 text-muted-foreground shrink-0' />
                                        </div>
                                      )}
                                    </a>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        )}
                    </div>

                    {/* Current User Avatar */}
                    {isCurrentUser && (
                      <div className='flex-shrink-0'>
                        <Avatar className='h-8 w-8'>
                          <AvatarImage src={senderPhotos[msg.sender_id]} />
                          <AvatarFallback className='bg-primary text-primary-foreground text-xs font-semibold'>
                            You
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    )}
                  </div>
                );
                return msg.id === firstNewMessageId
                  ? [<NewMessagesDivider key='new-replies-divider' />, row]
                  : row;
              })
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Message Input */}
      <Card className='mt-4'>
        <CardContent className='p-4'>
          <div className='space-y-3'>
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder='Type your message...'
              className='min-h-[80px] resize-none'
              disabled={isSubmitting}
            />

            <div className='flex items-center justify-between'>
              <div className='text-xs text-muted-foreground'>
                Press Enter to send, Shift+Enter for new line
              </div>

              <Button
                onClick={handleSendMessage}
                disabled={!message.trim() || isSubmitting}
                size='sm'
                className='h-8'
              >
                {isSubmitting ? (
                  <>
                    <div className='h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent' />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className='h-4 w-4 mr-2' />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialogs */}
      {showCloseConfirm && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <Card className='w-96'>
            <CardHeader>
              <CardTitle className='text-lg'>Mark as Resolved</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-sm text-muted-foreground'>
                Are you sure you want to mark this feedback as resolved? This will close the
                conversation and prevent further messages.
              </p>
              <div className='flex gap-2 justify-end'>
                <Button variant='outline' size='sm' onClick={() => setShowCloseConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant='default'
                  size='sm'
                  onClick={async () => {
                    if (onCloseFeedback) {
                      await onCloseFeedback();
                    }
                    setShowCloseConfirm(false);
                  }}
                >
                  Mark Resolved
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showFeatureRequestConfirm && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <Card className='w-[28rem]'>
            <CardHeader>
              <CardTitle className='text-lg flex items-center gap-2'>
                <Lightbulb className='h-5 w-5' />
                Move to Feature requests
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-sm text-muted-foreground'>
                This confirms the ask is a new feature or enhancement. It moves to the Feature
                requests section and no longer counts as open work for {reporterName}.
              </p>
              <div className='space-y-1.5'>
                <label htmlFor='feature-request-note' className='text-sm font-medium'>
                  Reply to {reporterName}
                </label>
                <Textarea
                  id='feature-request-note'
                  value={featureRequestNote}
                  onChange={e => setFeatureRequestNote(e.target.value)}
                  className='min-h-[96px] text-sm'
                />
                <p className='text-xs text-muted-foreground'>
                  Posted as a reply in this thread
                  {conversation.user_email && <> and emailed to {conversation.user_email}</>}.
                </p>
              </div>
              <div className='flex gap-2 justify-end'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => setShowFeatureRequestConfirm(false)}
                  disabled={isMovingToFeatureRequests}
                >
                  Cancel
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => handleConfirmMoveToFeatureRequests('')}
                  disabled={isMovingToFeatureRequests}
                >
                  Move
                </Button>
                <Button
                  variant='default'
                  size='sm'
                  onClick={() => handleConfirmMoveToFeatureRequests(featureRequestNote)}
                  disabled={isMovingToFeatureRequests || !featureRequestNote.trim()}
                >
                  Move and notify
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <Card className='w-96'>
            <CardHeader>
              <CardTitle className='text-lg text-destructive'>Delete Feedback</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-sm text-muted-foreground'>
                Are you sure you want to delete this feedback? This action cannot be undone and will
                permanently remove all messages and data.
              </p>
              <div className='flex gap-2 justify-end'>
                <Button variant='outline' size='sm' onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant='destructive'
                  size='sm'
                  onClick={async () => {
                    if (onDeleteFeedback) {
                      await onDeleteFeedback();
                    }
                    setShowDeleteConfirm(false);
                  }}
                >
                  Delete Forever
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {deletingMessageId && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <Card className='w-96'>
            <CardHeader>
              <CardTitle className='text-lg text-destructive'>Delete Message</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-sm text-muted-foreground'>
                Are you sure you want to delete this message? This action cannot be undone.
              </p>
              <div className='flex gap-2 justify-end'>
                <Button variant='outline' size='sm' onClick={() => setDeletingMessageId(undefined)}>
                  Cancel
                </Button>
                <Button variant='destructive' size='sm' onClick={handleConfirmDeleteMessage}>
                  Delete Message
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ConversationView;
